import { useCallback, useRef, useState } from 'react';
import { NavLink, useNavigate, Link } from 'react-router-dom';
import { NewCampaignModal } from '@/components/modals/NewCampaignModal';
import { NewCreativeModal } from '@/components/modals/NewCreativeModal';
import { DROPDOWN_ITEM, DROPDOWN_PANEL } from '@/components/primitives/dropdownMenuClasses';
import { useDismissOnOutsideClick } from '@/hooks/useDismissOnOutsideClick';
import { orgUiPath } from '@/core/api';
import { creativeEditorLinkState } from '@/rve/editorBackNav';
import { useOrgId } from '@/hooks/useOrgId';

export default function Sidebar() {
  const orgId = useOrgId();
  const navItems = [
    { to: orgUiPath(orgId, '/'), icon: 'dashboard', label: 'Dashboard', end: true },
    { to: orgUiPath(orgId, '/formats'), icon: 'grid_view', label: 'Formats', end: false },
    { to: orgUiPath(orgId, '/campaigns'), icon: 'campaign', label: 'Campaigns', end: false },
    { to: orgUiPath(orgId, '/creatives'), icon: 'auto_awesome', label: 'Creatives', end: false },
    { to: orgUiPath(orgId, '/renders'), icon: 'movie', label: 'Renders', end: false },
    { to: orgUiPath(orgId, '/assets'), icon: 'folder', label: 'Assets', end: false },
    { to: orgUiPath(orgId, '/analytics'), icon: 'leaderboard', label: 'Analytics', end: false },
  ];

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isNewCreativeOpen, setIsNewCreativeOpen] = useState(false);
  const [isNewCampaignOpen, setIsNewCampaignOpen] = useState(false);
  const newMenuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const closeDropdown = useCallback(() => setIsDropdownOpen(false), []);

  useDismissOnOutsideClick({
    isActive: isDropdownOpen,
    onDismiss: closeDropdown,
    containerRef: newMenuRef,
  });

  return (
    <aside className="aspectly:h-screen aspectly:w-64 aspectly:fixed aspectly:left-0 aspectly:top-0 aspectly:bg-white aspectly:border-r aspectly:border-stone-200 aspectly:hidden aspectly:md:flex aspectly:flex-col aspectly:p-6 aspectly:overflow-y-auto aspectly:z-40">
      <Link
        to={orgUiPath(orgId, '/')}
        className="aspectly:mb-10 aspectly:block aspectly:cursor-pointer aspectly:hover:opacity-90 aspectly:transition-opacity"
      >
        <img
          src="/aspectly-logo-horizontal.svg"
          alt="Aspectly"
          className="aspectly:h-9 aspectly:w-auto aspectly:max-w-[180px] aspectly:object-contain aspectly:shrink-0"
        />
      </Link>

      <div
        ref={newMenuRef}
        className="aspectly:pb-4 aspectly:mb-4 aspectly:border-b aspectly:border-stone-100 aspectly:relative"
      >
        <button
          type="button"
          onClick={() => setIsDropdownOpen((open) => !open)}
          className="aspectly:w-full aspectly:py-3 aspectly:bg-primary aspectly:text-on-primary aspectly:font-bold aspectly:rounded-xl aspectly:hover:bg-primary-dim aspectly:transition-all aspectly:shadow-md aspectly:shadow-primary/20 aspectly:flex aspectly:items-center aspectly:justify-center aspectly:gap-2 aspectly:cursor-pointer"
          aria-expanded={isDropdownOpen}
        >
          <span className="material-symbols-outlined aspectly:text-sm">add</span>
          New
        </button>
        {isDropdownOpen && (
          <div className={`${DROPDOWN_PANEL} aspectly:left-0 aspectly:w-full aspectly:z-[70]`}>
            <button
              type="button"
              onClick={() => {
                closeDropdown();
                setIsNewCreativeOpen(true);
              }}
              className={DROPDOWN_ITEM}
            >
              Creative
            </button>
            <button
              type="button"
              onClick={() => {
                closeDropdown();
                setIsNewCampaignOpen(true);
              }}
              className={DROPDOWN_ITEM}
            >
              Campaign
            </button>
          </div>
        )}
      </div>

      <nav className="aspectly:grow aspectly:space-y-1">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              `aspectly:flex aspectly:items-center aspectly:gap-3 aspectly:px-4 aspectly:py-3 aspectly:text-sm aspectly:font-bold aspectly:rounded-lg aspectly:transition-colors ${
                isActive
                  ? 'aspectly:text-primary aspectly:font-black aspectly:border-r-4 aspectly:border-primary aspectly:bg-primary-container'
                  : 'aspectly:text-on-surface-variant aspectly:hover:text-primary aspectly:hover:bg-primary-container'
              }`
            }
          >
            {({ isActive }) => (
              <>
                <span
                  className="material-symbols-outlined"
                  style={isActive ? { fontVariationSettings: "'FILL' 1" } : undefined}
                >
                  {item.icon}
                </span>
                {item.label}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <NewCampaignModal
        isOpen={isNewCampaignOpen}
        onClose={() => setIsNewCampaignOpen(false)}
        onCampaignCreated={() => navigate(orgUiPath(orgId, '/campaigns'))}
      />
      <NewCreativeModal
        isOpen={isNewCreativeOpen}
        onClose={() => setIsNewCreativeOpen(false)}
        onCreated={(created) =>
          navigate(orgUiPath(orgId, `/creatives/${created.id}`), {
            state: creativeEditorLinkState('creatives'),
          })
        }
      />
    </aside>
  );
}
